import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, X, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  CertificateCanvas,
  ElementsPanel,
  PropertiesPanel,
  EditorToolbar
} from '@/components/certificate-builder';
import certificateTemplateService from '@/services/certificate-template.service';
import {
  CertificateTemplateType,
  CertificateTemplateDto,
  CanvasElement,
  CanvasElementType,
  CanvasLayoutData,
  CanvasConfig,
  DEFAULT_TEXT_STYLE,
  createDefaultCertificateLayout,
  generateElementId,
  ELEMENT_PRESETS,
  TextCanvasElement,
  VariableCanvasElement,
  ImageCanvasElement,
  ShapeCanvasElement,
  LineCanvasElement,
  QRCodeCanvasElement,
  SignatureCanvasElement,
  generateSampleData,
  getDefaultAllowedVariables
} from '@/types/certificate-template.types';
import { parseHtmlToCanvasElements, SKILLZEN_LOGO_SIZE_CSS } from '@/utils/html-to-canvas-parser';
import { buildCanvasHtml, extractVariablesFromLayout } from '@/utils/canvas-to-html';
import UseTemplateConfirmModal from '@/components/certificate-builder/UseTemplateConfirmModal';
import { invalidateTemplateQueries } from '@/components/certificate-builder/templateQueries';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import type { NewTemplateDraft } from '@/components/certificate-builder/CreateTemplateModal';

/** CSS override injected into background iframe to hide text/images and show only decorative structure */
const BACKGROUND_OVERRIDE_CSS = `
html{background:transparent!important;}
body{margin:0!important;padding:0!important;display:block!important;min-height:auto!important;overflow:hidden!important;background:transparent!important;}
.certificate{background:transparent!important;}
*,[class],[style]{color:transparent!important;-webkit-text-fill-color:transparent!important;text-shadow:none!important;}
img,[class] img{opacity:0!important;}
.outer-border,.inner-border,.corner,.corner-tl,.corner-tr,.corner-bl,.corner-br{display:none!important;}
*:empty{border-color:transparent!important;background-color:transparent!important;}
.badge,.title-line,.divider,.score{background:transparent!important;background-color:transparent!important;box-shadow:none!important;border:none!important;}
div[style*="border-radius"]{background:transparent!important;background-color:transparent!important;box-shadow:none!important;}`;

/** Strip Handlebars conditionals, canvas editor artifacts, and data-canvas-var elements from HTML.
 *  Used for PREVIEW and PARSER contexts where we need flat HTML (no Handlebars). */
function stripEditorArtifacts(html: string): string {
  return html
    .replace(/\{\{#if\s+\w+\}\}([\s\S]*?)(?:\{\{else\}\}[\s\S]*?)?\{\{\/if\}\}/g, '$1')
    .replace(/<!--CANVAS_LAYOUT:[\s\S]*?:END_CANVAS_LAYOUT-->/g, '')
    .replace(/<!--CANVAS_VARS:[\s\S]*?:END_CANVAS_VARS-->/g, '')
    .replace(/<!--ORIGINAL_TEMPLATE:[\s\S]*?:END_ORIGINAL_TEMPLATE-->/g, '')
    .replace(/<div\s+data-canvas-var[^>]*>[\s\S]*?<\/div>/gi, '')
    .replace(/<div\s+data-canvas-injected[^>]*>[\s\S]*?<\/div>/gi, '');
}

/** Strip ONLY canvas editor comments — preserves {{#if}}/{{else}}/{{/if}} blocks intact.
 *  Used for SAVE context so backend DB retains Handlebars conditional logic.
 *  Also strips previously injected elements and accumulated !important inline styles
 *  to prevent stale elements and style drift across save cycles. */
function stripCanvasComments(html: string): string {
  let result = html
    .replace(/<!--CANVAS_LAYOUT:[\s\S]*?:END_CANVAS_LAYOUT-->/g, '')
    .replace(/<!--CANVAS_VARS:[\s\S]*?:END_CANVAS_VARS-->/g, '')
    .replace(/<!--ORIGINAL_TEMPLATE:[\s\S]*?:END_ORIGINAL_TEMPLATE-->/g, '')
    .replace(/<div\s+data-canvas-var[^>]*>[\s\S]*?<\/div>/gi, '')
    // Strip elements injected by previous save cycles (user-added elements).
    // Use [\s\S]*? to handle injected images with nested <img> tags.
    .replace(/<div\s+data-canvas-injected[^>]*>[\s\S]*?<\/div>/gi, '')
    // Strip border markers from previous save cycles
    .replace(/\s*data-canvas-border/gi, '');

  // Strip canvas-editor-injected !important properties from inline styles.
  // Now that we always start from pristine HTML, this is a safety net for legacy templates
  // that were saved before the ORIGINAL_TEMPLATE fix. For fresh pristine HTML, this is a no-op.
  // Only strip properties that also have the `!important` flag (original template properties
  // without !important are never touched).
  const canvasProps = /^(position|left|top|width|height|display|visibility|font-size|font-family|font-weight|font-style|text-decoration|text-align|line-height|color|background-color|z-index|overflow|opacity|margin)\s*:/i;
  result = result.replace(/(<[a-z][a-z0-9]*\b[^>]*?)\s*style="([^"]*)"([^>]*>)/gi,
    (_match, before: string, style: string, after: string) => {
      const cleanProps = style.split(';')
        .filter(p => !(p.includes('!important') && canvasProps.test(p.trim())))
        .map(p => p.trim())
        .filter(Boolean)
        .join(';');
      if (!cleanProps) return `${before}${after}`;
      return `${before} style="${cleanProps}"${after}`;
    }
  );

  return result;
}

/**
 * Full-page boxes (a class sized 842px × 595px, e.g. ".cert" in the built-in templates).
 * Their own white fill covers the page, so the Canvas background colour must be applied to them too.
 */
function findPageBoxSelectors(html: string): string[] {
  const css = (html.match(/<style[^>]*>[\s\S]*?<\/style>/gi) || []).join('\n').replace(/\/\*[\s\S]*?\*\//g, '');
  const found = new Set<string>();
  for (const [, selectors, body] of css.matchAll(/([^{}]+)\{([^{}]*)\}/g)) {
    if (!/(^|;)\s*width\s*:\s*842px/i.test(body) || !/(^|;)\s*height\s*:\s*595px/i.test(body)) continue;
    selectors.split(',').map(s => s.trim()).filter(s => /^\.[\w-]+$/.test(s) && s !== '.certificate').forEach(s => found.add(s));
  }
  return [...found];
}

/** Prepare background HTML from original template — hides text/images, shows only decorative elements */
function buildBackgroundHtml(originalHtml: string): string {
  const stripped = stripEditorArtifacts(originalHtml);
  // Page boxes see-through (fill colour only) so the chosen Canvas colour shows while editing
  const pageBoxes = findPageBoxSelectors(stripped);
  const overrideCss = BACKGROUND_OVERRIDE_CSS + SKILLZEN_LOGO_SIZE_CSS + (pageBoxes.length ? `\n${pageBoxes.join(',')}{background-color:transparent!important;}` : '');
  if (stripped.includes('</style>')) {
    return stripped.replace('</style>', overrideCss + '\n</style>');
  } else if (stripped.includes('</head>')) {
    return stripped.replace('</head>', `\n<style>${overrideCss}</style>\n</head>`);
  } else {
    return stripped + `\n<style>${overrideCss}</style>`;
  }
}

/** "Blank canvas" for Create Template: an empty A4 landscape page; every element the admin adds is written on Save */
const BLANK_CERTIFICATE_HTML = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
*{margin:0;padding:0;box-sizing:border-box}
html,body{width:842px;height:595px;overflow:hidden;background:#FFFFFF}
body{position:relative;font-family:Georgia,serif;color:#111827}
</style>
</head>
<body>
</body>
</html>`;

interface HistoryState {
  past: CanvasLayoutData[];
  present: CanvasLayoutData;
  future: CanvasLayoutData[];
}

export default function CertificateTemplateBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditMode = !!id;
  const location = useLocation();
  const routeState = location.state as { newTemplate?: NewTemplateDraft; offerSwitch?: boolean } | null;
  // Create mode: opened from "Create Template"; nothing is in the database until the first Save
  const newTemplate = isEditMode ? undefined : routeState?.newTemplate;
  const isCreateMode = !!newTemplate;

  // Template metadata
  const [templateName, setTemplateName] = useState('Untitled Template');
  const [templateType, setTemplateType] = useState<CertificateTemplateType | null>(
    isEditMode ? null : newTemplate?.template_type ?? CertificateTemplateType.COURSE_COMPLETION
  );
  // Set by the first Save of a new template (see the offerSwitch effect below)
  const [showUseConfirm, setShowUseConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Canvas state with history
  const [history, setHistory] = useState<HistoryState>({
    past: [],
    present: createDefaultCertificateLayout(CertificateTemplateType.COURSE_COMPLETION),
    future: []
  });

  // UI state
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [templateReady, setTemplateReady] = useState(!isEditMode && !isCreateMode);
  const [backgroundHtml, setBackgroundHtml] = useState<string | null>(null);
  // Original html_template from API — needed for Restore after Clear All (even after save + reopen)
  const [originalHtmlTemplate, setOriginalHtmlTemplate] = useState<string | null>(null);
  // Pristine original HTML (before any canvas modifications) — used as clean base for every save.
  // Prevents cumulative style drift: each save starts from this clean base, strips nothing, applies fresh.
  const [pristineHtmlTemplate, setPristineHtmlTemplate] = useState<string | null>(null);
  const [isCleared, setIsCleared] = useState(false);
  // Initial parsed styles — used to detect what the user actually changed
  const [initialElementStyles, setInitialElementStyles] = useState<Record<string, any>>({});
  // Initial border config JSON — used to detect if user changed border properties.
  // When unchanged, original HTML borders are preserved exactly (no color/style drift).
  const [initialBorderJson, setInitialBorderJson] = useState<string>('');
  const [initialBackgroundJson, setInitialBackgroundJson] = useState<string>('');
  // Plain page border as first opened; undefined = template saved before this was tracked
  const [initialSimpleBorderJson, setInitialSimpleBorderJson] = useState<string | undefined>(undefined);

  // Derived state
  const layoutData = history.present;
  const selectedElement = useMemo(
    () => layoutData.elements.find(el => el.id === selectedElementId) || null,
    [layoutData.elements, selectedElementId]
  );

  // Fetch template for edit mode
  const { data: savedTemplate, isError: savedTemplateFailed } = useQuery({
    queryKey: ['certificateTemplate', id],
    queryFn: () => certificateTemplateService.getTemplateById(id!),
    enabled: isEditMode
  });

  // Create mode "copy of the design in use now": load that template's design
  const sourceTemplateId = newTemplate?.source_template_id;
  const { data: sourceTemplate, isError: sourceTemplateFailed } = useQuery({
    queryKey: ['certificateTemplate', sourceTemplateId],
    queryFn: () => certificateTemplateService.getTemplateById(sourceTemplateId!),
    enabled: isCreateMode && !!sourceTemplateId
  });

  // What the editor opens: the saved template (edit), a copy of the design in use, or a blank page (create)
  const newType = newTemplate?.template_type;
  const newName = newTemplate?.template_name;
  const existingTemplate = useMemo<CertificateTemplateDto | undefined>(() => {
    if (isEditMode) return savedTemplate;
    if (!newType || !newName) return undefined;
    if (sourceTemplateId && !sourceTemplate) return undefined;
    return {
      _id: '', template_key: '', template_name: newName, template_type: newType,
      html_template: sourceTemplate?.html_template || BLANK_CERTIFICATE_HTML,
      allowed_variables: sourceTemplate?.allowed_variables || getDefaultAllowedVariables(newType),
      is_active: false, is_default: false, created_at: '', updated_at: ''
    };
  }, [isEditMode, savedTemplate, newType, newName, sourceTemplateId, sourceTemplate]);
  const isBlankStart = isCreateMode && !sourceTemplateId;

  // Create route opened without "Create Template" (e.g. typed URL) → back to the list
  useEffect(() => {
    if (!isEditMode && !isCreateMode) {
      toast.error('Choose a template type first.');
      navigate('/admin/enterprise/certificate-templates', { replace: true });
    }
  }, [isEditMode, isCreateMode, navigate]);

  // First Save of a new template reopens it here → ask whether to use it now. An effect, not
  // initial state: the router keeps this component mounted when moving from create to edit.
  // One-time: the flag is dropped from history so a refresh doesn't ask again.
  useEffect(() => {
    if (!isEditMode || !routeState?.offerSwitch) return;
    setShowUseConfirm(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [isEditMode, routeState?.offerSwitch, location.pathname, navigate]);

  // Generate sample data for preview
  const sampleData = useMemo(() => {
    const allowedVariables = existingTemplate?.allowed_variables
      || getDefaultAllowedVariables(templateType);
    return generateSampleData(allowedVariables);
  }, [templateType, existingTemplate?.allowed_variables]);

  // Manage body scroll to prevent double scrollbars when full-screen builder is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Load existing template data
  useEffect(() => {
    if (!existingTemplate) return;

    setTemplateName(existingTemplate.template_name);
    setTemplateType(existingTemplate.template_type);

    // Check for embedded canvas layout JSON (saved by canvas builder)
    let canvasLayout: CanvasLayoutData | null = null;
    if (existingTemplate.html_template) {
      const layoutMatch = existingTemplate.html_template.match(
        /<!--CANVAS_LAYOUT:([\s\S]*?):END_CANVAS_LAYOUT-->/
      );
      if (layoutMatch) {
        try {
          canvasLayout = JSON.parse(layoutMatch[1]);
        } catch (e) {
          // Invalid embedded layout JSON — fall through to HTML parsing
        }
      }
    }

    // Always store the original API html_template for restore capability
    // Check if pristine original was embedded in a previous canvas save
    const origMatch = existingTemplate.html_template?.match(
      /<!--ORIGINAL_TEMPLATE:([\s\S]*?):END_ORIGINAL_TEMPLATE-->/
    );
    const origHtml = origMatch ? decodeURIComponent(escape(atob(origMatch[1]))) : existingTemplate.html_template;
    setOriginalHtmlTemplate(origHtml || null);
    // Pristine HTML = the clean original (before any canvas modifications).
    // Used as the base for every save so style changes are always applied fresh.
    setPristineHtmlTemplate(origHtml || null);

    if (canvasLayout) {
      // Fill missing decorativeBorder (or create it) from original HTML CSS
      if (origHtml && (origHtml.includes('outer-border') || origHtml.includes('inner-border') || origHtml.includes('corner'))) {
        if (!canvasLayout.canvas.decorativeBorder) {
          canvasLayout.canvas.decorativeBorder = {};
        }
        const db = canvasLayout.canvas.decorativeBorder;
        if (!db.outer && origHtml.includes('outer-border')) {
          // Extract outer border color from CSS: border:Npx style #color
          const outerMatch = origHtml.match(/\.outer-border\s*\{[^}]*border:\s*(\d+)px\s+(\w+)\s+(#[0-9a-fA-F]+)/);
          const outerInsetMatch = origHtml.match(/\.outer-border\s*\{[^}]*top:\s*(\d+)px[^}]*left:\s*(\d+)px[^}]*right:\s*(\d+)px[^}]*bottom:\s*(\d+)px/);
          db.outer = {
            enabled: true,
            width: outerMatch ? parseInt(outerMatch[1]) : 1,
            style: (outerMatch?.[2] || 'solid') as any,
            color: outerMatch?.[3] || db.inner?.color || '#3B82F6',
            inset: {
              top: outerInsetMatch ? parseInt(outerInsetMatch[1]) : 25,
              left: outerInsetMatch ? parseInt(outerInsetMatch[2]) : 35,
              right: outerInsetMatch ? parseInt(outerInsetMatch[3]) : 35,
              bottom: outerInsetMatch ? parseInt(outerInsetMatch[4]) : 25
            }
          };
        }
        if (!db.inner && origHtml.includes('inner-border')) {
          const innerMatch = origHtml.match(/\.inner-border\s*\{[^}]*border:\s*(\d+)px\s+(\w+)\s+(#[0-9a-fA-F]+)/);
          const innerInsetMatch = origHtml.match(/\.inner-border\s*\{[^}]*top:\s*(\d+)px[^}]*left:\s*(\d+)px[^}]*right:\s*(\d+)px[^}]*bottom:\s*(\d+)px/);
          db.inner = {
            enabled: true,
            width: innerMatch ? parseInt(innerMatch[1]) : 2,
            style: (innerMatch?.[2] || 'solid') as any,
            color: innerMatch?.[3] || '#1E40AF',
            inset: {
              top: innerInsetMatch ? parseInt(innerInsetMatch[1]) : 35,
              left: innerInsetMatch ? parseInt(innerInsetMatch[2]) : 45,
              right: innerInsetMatch ? parseInt(innerInsetMatch[3]) : 45,
              bottom: innerInsetMatch ? parseInt(innerInsetMatch[4]) : 35
            }
          };
        }
      }

      // Template was previously saved via canvas builder — load directly
      setHistory({ past: [], present: canvasLayout, future: [] });
      // Use saved initial border/background from ORIGINAL parse (not current canvas state).
      // This ensures change detection works correctly after save→reload — the initial values
      // represent the ORIGINAL template, not the user's modifications.
      setInitialBorderJson(canvasLayout._initialBorderJson ?? JSON.stringify(canvasLayout.canvas.decorativeBorder || {}));
      setInitialBackgroundJson(canvasLayout._initialBackgroundJson ?? JSON.stringify(canvasLayout.canvas.background || {}));
      setInitialSimpleBorderJson(canvasLayout._initialSimpleBorderJson);
      if (canvasLayout.elements.length === 0 && origHtml) {
        setIsCleared(true);
      }

      // Prepare background HTML from original template (decorative borders, corners, etc.)
      if (origHtml) {
        setBackgroundHtml(buildBackgroundHtml(origHtml));

        // Store initial styles for change detection.
        // Prefer saved _initialElementStyles (from the original HTML parse) so change detection
        // works correctly across save→reload cycles. Without this, initialElementStyles would
        // come from the CANVAS_LAYOUT elements (which already have user's modifications), and
        // applyCanvasStylesToHtml would see no changes → lose all style modifications.
        if (canvasLayout._initialElementStyles && Object.keys(canvasLayout._initialElementStyles).length > 0) {
          setInitialElementStyles(canvasLayout._initialElementStyles);
        } else {
          // Fallback for templates saved before this fix — compute from current canvas elements
          const stylesMap: Record<string, any> = {};
          canvasLayout.elements.forEach((el: CanvasElement) => {
            if (el.type === CanvasElementType.TEXT || el.type === CanvasElementType.VARIABLE) {
              const key = el.type === CanvasElementType.TEXT
                ? (el as TextCanvasElement).content
                : (el as VariableCanvasElement).placeholder || `{{${(el as VariableCanvasElement).variableKey}}}`;
              if (!stylesMap[key]) {
                stylesMap[key] = { ...(el as any).style, position: { ...el.position } };
              }
            } else if (el.type === CanvasElementType.LINE) {
              stylesMap[`__line__${el.id}`] = { strokeColor: (el as any).strokeColor, position: { ...el.position } };
            }
          });
          setInitialElementStyles(stylesMap);
        }
      }
      setTemplateReady(true);
    } else if (existingTemplate.html_template) {
      setBackgroundHtml(buildBackgroundHtml(existingTemplate.html_template));

      // Parse HTML template into canvas elements (async — renders in hidden iframe)
      parseHtmlToCanvasElements(existingTemplate.html_template).then(parsed => {
        // Store initial styles for change detection on save.
        // Use placeholder for variables to match the actual HTML text content.
        const stylesMap: Record<string, any> = {};
        parsed.elements.forEach(el => {
          if (el.type === CanvasElementType.TEXT || el.type === CanvasElementType.VARIABLE) {
            const key = el.type === CanvasElementType.TEXT
              ? (el as TextCanvasElement).content
              : (el as VariableCanvasElement).placeholder || `{{${(el as VariableCanvasElement).variableKey}}}`;
            if (!stylesMap[key]) {
              stylesMap[key] = { ...(el as any).style, position: { ...el.position } };
            }
          } else if (el.type === CanvasElementType.LINE) {
            stylesMap[`__line__${el.id}`] = { strokeColor: (el as any).strokeColor, position: { ...el.position } };
          }
        });
        setInitialElementStyles(stylesMap);
        setInitialBorderJson(JSON.stringify(parsed.canvasConfig.decorativeBorder || {}));
        setInitialBackgroundJson(JSON.stringify(parsed.canvasConfig.background || {}));
        setInitialSimpleBorderJson(JSON.stringify(parsed.canvasConfig.border || {}));
        setHistory({
          past: [],
          present: {
            version: '1.0.0',
            canvas: parsed.canvasConfig,
            elements: parsed.elements,
            guides: { horizontal: [], vertical: [] },
            gridEnabled: true,
            snapToGrid: true,
            gridSize: 10
          },
          future: []
        });
        setTemplateReady(true);
      }).catch(() => {
        setHistory({
          past: [],
          present: createDefaultCertificateLayout(existingTemplate.template_type),
          future: []
        });
        setTemplateReady(true);
        toast.error('Failed to parse template — loaded default layout');
      });
    } else {
      // No template HTML — start with default layout
      setHistory({
        past: [],
        present: createDefaultCertificateLayout(existingTemplate.template_type),
        future: []
      });
      setTemplateReady(true);
    }
  }, [existingTemplate]);

  // Update layout with history tracking
  const updateLayout = useCallback((newLayout: CanvasLayoutData) => {
    setHistory(prev => ({
      past: [...prev.past.slice(-49), prev.present],
      present: newLayout,
      future: []
    }));
  }, []);

  // Undo/Redo
  const handleUndo = useCallback(() => {
    setHistory(prev => {
      if (prev.past.length === 0) return prev;
      const previous = prev.past[prev.past.length - 1];
      return {
        past: prev.past.slice(0, -1),
        present: previous,
        future: [prev.present, ...prev.future]
      };
    });
  }, []);

  const handleRedo = useCallback(() => {
    setHistory(prev => {
      if (prev.future.length === 0) return prev;
      const next = prev.future[0];
      return {
        past: [...prev.past, prev.present],
        present: next,
        future: prev.future.slice(1)
      };
    });
  }, []);

  // Element operations
  const handleAddElement = useCallback((elementData: any, dropPosition?: { x: number; y: number }) => {
    let newElement: CanvasElement;

    const getPosition = (elWidth: number, elHeight: number) => {
      if (dropPosition) {
        return {
          x: Math.max(0, dropPosition.x - elWidth / 2),
          y: Math.max(0, dropPosition.y - elHeight / 2)
        };
      }
      return {
        x: (layoutData.canvas.width - elWidth) / 2,
        y: (layoutData.canvas.height - elHeight) / 2
      };
    };

    // Check if it's a preset
    if (elementData.preset && ELEMENT_PRESETS[elementData.preset as keyof typeof ELEMENT_PRESETS]) {
      const preset = ELEMENT_PRESETS[elementData.preset as keyof typeof ELEMENT_PRESETS];

      if (preset.type === CanvasElementType.TEXT) {
        const pos = getPosition(300, 50);
        newElement = {
          id: generateElementId(),
          type: CanvasElementType.TEXT,
          name: preset.name,
          content: (preset as any).content,
          style: (preset as any).style,
          position: { ...pos, width: 300, height: 50, zIndex: layoutData.elements.length + 1 },
          locked: false,
          visible: true,
          opacity: 1
        } as TextCanvasElement;
      } else if (preset.type === CanvasElementType.VARIABLE) {
        const pos = getPosition(300, 50);
        newElement = {
          id: generateElementId(),
          type: CanvasElementType.VARIABLE,
          name: preset.name,
          variableKey: (preset as any).variableKey,
          placeholder: (preset as any).placeholder,
          style: (preset as any).style,
          format: (preset as any).format || 'text',
          position: { ...pos, width: 300, height: 50, zIndex: layoutData.elements.length + 1 },
          locked: false,
          visible: true,
          opacity: 1
        } as VariableCanvasElement;
      } else if (preset.type === CanvasElementType.SIGNATURE) {
        const pos = getPosition(200, 80);
        newElement = {
          id: generateElementId(),
          type: CanvasElementType.SIGNATURE,
          name: preset.name,
          signatureType: (preset as any).signatureType,
          text: (preset as any).text,
          style: (preset as any).style,
          showLabel: (preset as any).showLabel,
          label: (preset as any).label,
          position: { ...pos, width: 200, height: 80, zIndex: layoutData.elements.length + 1 },
          locked: false,
          visible: true,
          opacity: 1
        } as SignatureCanvasElement;
      } else if (preset.type === CanvasElementType.QR_CODE) {
        const pos = getPosition(100, 100);
        newElement = {
          id: generateElementId(),
          type: CanvasElementType.QR_CODE,
          name: preset.name,
          dataKey: (preset as any).dataKey,
          size: (preset as any).size,
          foregroundColor: (preset as any).foregroundColor,
          backgroundColor: (preset as any).backgroundColor,
          errorCorrectionLevel: (preset as any).errorCorrectionLevel,
          position: { ...pos, width: 100, height: 100, zIndex: layoutData.elements.length + 1 },
          locked: false,
          visible: true,
          opacity: 1
        } as QRCodeCanvasElement;
      } else if (preset.type === CanvasElementType.LINE) {
        const pos = getPosition(200, 10);
        newElement = {
          id: generateElementId(),
          type: CanvasElementType.LINE,
          name: preset.name,
          strokeWidth: (preset as any).strokeWidth,
          strokeColor: (preset as any).strokeColor,
          strokeStyle: (preset as any).strokeStyle,
          startPoint: { x: 0, y: 0 },
          endPoint: { x: 200, y: 0 },
          position: { ...pos, width: 200, height: 10, zIndex: layoutData.elements.length + 1 },
          locked: false,
          visible: true,
          opacity: 1
        } as LineCanvasElement;
      } else {
        return;
      }
    } else {
      // Create from data
      switch (elementData.type) {
        case CanvasElementType.TEXT: {
          const pos = getPosition(200, 40);
          newElement = {
            id: generateElementId(),
            type: CanvasElementType.TEXT,
            name: 'Text',
            content: elementData.data?.content || 'New Text',
            style: elementData.data?.style || DEFAULT_TEXT_STYLE,
            position: { ...pos, width: 200, height: 40, zIndex: layoutData.elements.length + 1 },
            locked: false,
            visible: true,
            opacity: 1
          } as TextCanvasElement;
          break;
        }

        case CanvasElementType.VARIABLE: {
          const pos = getPosition(200, 40);
          newElement = {
            id: generateElementId(),
            type: CanvasElementType.VARIABLE,
            name: elementData.data?.variableKey?.replace(/_/g, ' ') || 'Variable',
            variableKey: elementData.data?.variableKey || 'variable',
            placeholder: elementData.data?.placeholder || 'Value',
            style: elementData.data?.style || DEFAULT_TEXT_STYLE,
            format: elementData.data?.format || 'text',
            position: { ...pos, width: 200, height: 40, zIndex: layoutData.elements.length + 1 },
            locked: false,
            visible: true,
            opacity: 1
          } as VariableCanvasElement;
          break;
        }

        case CanvasElementType.IMAGE: {
          const pos = getPosition(150, 100);
          newElement = {
            id: generateElementId(),
            type: CanvasElementType.IMAGE,
            name: 'Image',
            src: elementData.data?.src || '',
            alt: elementData.data?.alt || 'Image',
            fit: elementData.data?.fit || 'contain',
            position: { ...pos, width: 150, height: 100, zIndex: layoutData.elements.length + 1 },
            locked: false,
            visible: true,
            opacity: 1
          } as ImageCanvasElement;
          break;
        }

        case CanvasElementType.SHAPE: {
          const pos = getPosition(100, 100);
          newElement = {
            id: generateElementId(),
            type: CanvasElementType.SHAPE,
            name: 'Shape',
            shapeType: elementData.data?.shapeType || 'rectangle',
            fill: elementData.data?.fill || '#E5E7EB',
            border: elementData.data?.border,
            position: { ...pos, width: 100, height: 100, zIndex: layoutData.elements.length + 1 },
            locked: false,
            visible: true,
            opacity: 1
          } as ShapeCanvasElement;
          break;
        }

        case CanvasElementType.LINE: {
          const pos = getPosition(200, 10);
          newElement = {
            id: generateElementId(),
            type: CanvasElementType.LINE,
            name: 'Line',
            strokeWidth: elementData.data?.strokeWidth || 2,
            strokeColor: elementData.data?.strokeColor || '#000000',
            strokeStyle: elementData.data?.strokeStyle || 'solid',
            startPoint: elementData.data?.startPoint || { x: 0, y: 0 },
            endPoint: elementData.data?.endPoint || { x: 200, y: 0 },
            position: { ...pos, width: 200, height: 10, zIndex: layoutData.elements.length + 1 },
            locked: false,
            visible: true,
            opacity: 1
          } as LineCanvasElement;
          break;
        }

        case CanvasElementType.QR_CODE: {
          const pos = getPosition(100, 100);
          newElement = {
            id: generateElementId(),
            type: CanvasElementType.QR_CODE,
            name: 'QR Code',
            dataKey: elementData.data?.dataKey || 'verification_url',
            size: elementData.data?.size || 80,
            foregroundColor: elementData.data?.foregroundColor || '#000000',
            backgroundColor: elementData.data?.backgroundColor || '#FFFFFF',
            errorCorrectionLevel: elementData.data?.errorCorrectionLevel || 'M',
            position: { ...pos, width: 100, height: 100, zIndex: layoutData.elements.length + 1 },
            locked: false,
            visible: true,
            opacity: 1
          } as QRCodeCanvasElement;
          break;
        }

        default:
          return;
      }
    }

    updateLayout({
      ...layoutData,
      elements: [...layoutData.elements, newElement]
    });
    setSelectedElementId(newElement.id);
  }, [layoutData, updateLayout]);

  const handleDropElement = useCallback((elementData: any, position: { x: number; y: number }) => {
    handleAddElement(elementData, position);
  }, [handleAddElement]);

  const handleUpdateElement = useCallback((id: string, updates: Partial<CanvasElement>) => {
    updateLayout({
      ...layoutData,
      elements: layoutData.elements.map(el =>
        el.id === id ? { ...el, ...updates } as CanvasElement : el
      )
    });
  }, [layoutData, updateLayout]);

  const handleDeleteElement = useCallback((id: string) => {
    updateLayout({
      ...layoutData,
      elements: layoutData.elements.filter(el => el.id !== id)
    });
    if (selectedElementId === id) {
      setSelectedElementId(null);
    }
  }, [layoutData, updateLayout, selectedElementId]);

  const handleDuplicateElement = useCallback((id: string) => {
    const element = layoutData.elements.find(el => el.id === id);
    if (!element) return;

    const newElement: CanvasElement = {
      ...element,
      id: generateElementId(),
      name: `${element.name} (copy)`,
      position: {
        ...element.position,
        x: element.position.x + 20,
        y: element.position.y + 20,
        zIndex: layoutData.elements.length + 1
      }
    } as CanvasElement;

    updateLayout({
      ...layoutData,
      elements: [...layoutData.elements, newElement]
    });
    setSelectedElementId(newElement.id);
  }, [layoutData, updateLayout]);

  const handleUpdateCanvas = useCallback((config: Partial<CanvasConfig>) => {
    updateLayout({
      ...layoutData,
      canvas: { ...layoutData.canvas, ...config }
    });
  }, [layoutData, updateLayout]);

  // Clear all content — remove overlay elements + background decorations
  const handleClearContent = useCallback(() => {
    updateLayout({
      ...layoutData,
      elements: [],
      canvas: {
        ...layoutData.canvas,
        decorativeBorder: undefined
      }
    });
    setSelectedElementId(null);
    setBackgroundHtml(null);
    setIsCleared(true);
    toast.success('All content cleared. Click "Restore" to undo.');
  }, [layoutData, updateLayout]);

  // Restore original template from API
  const handleRestoreContent = useCallback(() => {
    if (!originalHtmlTemplate) return;

    setBackgroundHtml(buildBackgroundHtml(originalHtmlTemplate));

    // Re-parse the original HTML to get elements back
    setTemplateReady(false);
    parseHtmlToCanvasElements(originalHtmlTemplate).then(parsed => {
      updateLayout({
        ...layoutData,
        canvas: parsed.canvasConfig,
        elements: parsed.elements
      });
      setIsCleared(false);
      setTemplateReady(true);
      toast.success('Template restored.');
    }).catch(() => {
      setTemplateReady(true);
      toast.error('Failed to restore template.');
    });
  }, [originalHtmlTemplate, layoutData, updateLayout]);

  // Layer operations
  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => certificateTemplateService.updateTemplate(id!, data),
    onSuccess: () => {
      toast.success('Certificate template updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['certificateTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['certificateTemplate', id] });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to update template');
    }
  });

  // Create mutation — first Save of a new template; reopens it in edit mode and asks whether to use it
  const createMutation = useMutation({
    mutationFn: (data: { template_name: string; template_type: CertificateTemplateType; html_template: string; allowed_variables: string[] }) =>
      certificateTemplateService.createDraftTemplate(data),
    onSuccess: (created) => {
      toast.success('Certificate template saved.');
      // New draft: lists and this type's picker change; the default and the trash don't
      invalidateTemplateQueries(queryClient, created.template_type, { inUse: false, deletedCount: false });
      navigate(`/admin/enterprise/certificate-templates/${created._id}/edit`, { replace: true, state: { offerSwitch: true } });
    },
    onError: (err: Error) => {
      toast.error(/already exists/i.test(err.message)
        ? 'A template with this name already exists. Exit and create it again with a different name.'
        : err.message || 'Failed to save template');
    }
  });

  // Rename from the top bar. A saved template is renamed at once (only the name is sent, so
  // unsaved design changes stay on the canvas); a new one just takes the name into its first Save.
  const handleRename = useCallback(async (name: string) => {
    if (await certificateTemplateService.isTemplateNameTaken(name)) {
      throw new Error('A template with this name already exists. Please use a different name.');
    }
    if (isEditMode && id) {
      try {
        await certificateTemplateService.updateTemplate(id, { template_name: name });
      } catch (err) {
        throw /already exists/i.test((err as Error).message)
          ? new Error('A template with this name already exists. Please use a different name.')
          : err;
      }
      // Lists and pickers only: refetching this template's own query would reload the canvas
      invalidateTemplateQueries(queryClient, templateType ?? undefined, { inUse: true, deletedCount: false });
      toast.success(`Template renamed to "${name}".`);
    }
    setTemplateName(name);
  }, [isEditMode, id, templateType, queryClient]);

  // Validation
  const validateForm = (): boolean => {
    if (!templateName.trim()) {
      toast.error('Template name is required');
      return false;
    }
    return true;
  };

  /**
   * Apply ONLY changed style properties to matching elements in the original HTML.
   * Compares current canvas styles with initial parsed styles — only overrides what changed.
   * This preserves the CSS class layout (margins, padding, etc.) while applying user edits.
   */
  const applyCanvasStylesToHtml = useCallback((html: string, elements: CanvasElement[]): string => {
    let result = html;

    // Only the first element per unique key patches the original HTML.
    // Duplicates (user added same variable again) are handled by injection below.
    const patchedKeys = new Set<string>();
    // Track elements whose position changed — these must NOT get position:absolute
    // on the existing nested element (canvas coordinates are body-relative but the
    // element's containing block may be a deeply nested positioned ancestor, causing
    // wrong placement). Instead, the original is hidden and re-injected at body level.
    const posChangedKeys = new Set<string>();

    for (const el of elements) {
      let searchText: string | null = null;
      let currentStyle: any = null;

      if (el.type === CanvasElementType.TEXT) {
        const textEl = el as TextCanvasElement;
        searchText = textEl.content;
        currentStyle = textEl.style;
      } else if (el.type === CanvasElementType.VARIABLE) {
        const varEl = el as VariableCanvasElement;
        // Use placeholder (e.g. "Score: {{percentage}}%") to match actual HTML text content.
        // Falls back to {{variableKey}} for exact-match variables like {{holder_name}}.
        searchText = varEl.placeholder || `{{${varEl.variableKey}}}`;
        currentStyle = varEl.style;
      }

      if (!searchText || !currentStyle) continue;

      // Get original parsed style for this element
      const origStyle = initialElementStyles[searchText];
      if (!origStyle) continue; // no original → skip (handled by injection below)

      // Skip duplicate keys — only first element patches the original HTML tag
      if (patchedKeys.has(searchText)) continue;
      patchedKeys.add(searchText);

      // Build inline style changes. Only override properties that the user actually changed
      // in the editor. This preserves the original CSS-class layout (flexbox column centering,
      // margins, padding) so the certificate renders correctly with variable-length content.
      const changes: string[] = [];

      // Style changes (only when different from original)
      if (currentStyle.color !== origStyle.color) changes.push(`color:${currentStyle.color} !important`);
      if (currentStyle.fontSize !== origStyle.fontSize) changes.push(`font-size:${currentStyle.fontSize}px !important`);
      if (currentStyle.fontWeight !== origStyle.fontWeight) changes.push(`font-weight:${currentStyle.fontWeight} !important`);
      if (currentStyle.fontFamily !== origStyle.fontFamily) changes.push(`font-family:${(currentStyle.fontFamily || '').replace(/"/g, "'")} !important`);
      if (currentStyle.fontStyle !== origStyle.fontStyle) changes.push(`font-style:${currentStyle.fontStyle} !important`);
      if (currentStyle.textAlign !== origStyle.textAlign) changes.push(`text-align:${currentStyle.textAlign} !important`);
      if (currentStyle.textDecoration !== origStyle.textDecoration) changes.push(`text-decoration:${currentStyle.textDecoration} !important`);
      if (currentStyle.backgroundColor && currentStyle.backgroundColor !== origStyle.backgroundColor) changes.push(`background-color:${currentStyle.backgroundColor} !important`);
      if (currentStyle.borderRadius != null && currentStyle.borderRadius !== origStyle.borderRadius) changes.push(`border-radius:${currentStyle.borderRadius}px !important`);

      // Only apply absolute positioning if the user actually moved or resized the element.
      // Otherwise, preserve the original CSS layout (flexbox centering, flow positioning)
      // which handles variable-length content gracefully.
      const currentPos = el.position;
      const origPos = origStyle.position;
      const posChanged = origPos && (
        Math.round(currentPos.x) !== Math.round(origPos.x) ||
        Math.round(currentPos.y) !== Math.round(origPos.y) ||
        Math.round(currentPos.width) !== Math.round(origPos.width) ||
        Math.round(currentPos.height) !== Math.round(origPos.height)
      );

      if (posChanged) {
        // Canvas coordinates are relative to the body / .certificate container.
        // But the target element may be deeply nested inside positioned ancestors
        // (e.g., a flex row inside an absolute container). Adding position:absolute
        // with body-relative coords to such a nested element positions it relative
        // to the wrong containing block, making it invisible.
        // Fix: hide the original element in-place and let the injection section
        // (below) create a correctly-positioned data-canvas-injected div at body level.
        // Use visibility:hidden (not display:none) to preserve the element's space
        // in flex layouts. display:none collapses the space and breaks alignment.
        changes.push(`visibility:hidden !important`);
        posChangedKeys.add(searchText);
      }

      // Skip if no changes were made (user didn't modify this element at all)
      if (changes.length === 0) continue;

      const inlineStyle = changes.join(';');

      // Find ALL HTML elements containing this exact text and add/update style.
      // Uses 'gi' flag to patch both {{#if}} and {{else}} branches where same text appears.
      const escaped = searchText.replace(/[{}.*+?^$|[\]\\()]/g, '\\$&');
      const pattern = new RegExp(`(<[a-z][a-z0-9]*\\b[^>]*?)(>\\s*${escaped}\\s*</)`, 'gi');

      result = result.replace(pattern, (_, openTag: string, rest: string) => {
        // Check if tag already has a style attribute
        const existingStyleMatch = openTag.match(/style="([^"]*)"/);
        if (existingStyleMatch) {
          // Append changes to existing style
          return `${openTag.replace(/style="[^"]*"/, `style="${existingStyleMatch[1]};${inlineStyle}"`)}${rest}`;
        }
        return `${openTag} style="${inlineStyle}"${rest}`;
      });
    }

    // Inject HTML for newly added elements (not from original parse).
    // These have no matching tag in the original HTML, so we generate absolute-positioned divs.
    // A element is "new" if its key has no initial style OR its key is a duplicate (already claimed).
    const claimedKeys = new Set<string>();
    const newElementHtmlParts: string[] = [];

    // Every static image in the original HTML is on the canvas as an IMAGE element and is
    // re-injected below from canvas state, so hide the originals (same approach as LINE).
    // Without this each image printed twice, and deleting one on canvas left the original.
    result = result.replace(/<img\b[^>]*>/gi, (tag) => {
      const src = tag.match(/\bsrc\s*=\s*["']([^"']*)["']/i)?.[1] || '';
      if (!src || src.startsWith('{{')) return tag; // variable images handled separately
      if (/visibility\s*:\s*hidden/i.test(tag)) return tag;
      if (/\bstyle\s*=\s*"/i.test(tag)) {
        return tag.replace(/\bstyle\s*=\s*"([^"]*)"/i, 'style="$1;visibility:hidden!important"');
      }
      return tag.replace(/^<img\b/i, '<img style="visibility:hidden!important"');
    });

    // Lines are canvas-managed when any exist on canvas or were found on first parse
    // (__line__ keys) — then hide every original line, so deleted lines stay deleted.
    const linesManaged = elements.some(e => e.type === CanvasElementType.LINE)
      || Object.keys(initialElementStyles).some(k => k.startsWith('__line__'));
    if (linesManaged) {
      const hideTag = (open: string) => /style="/.test(open)
        ? open.replace(/style="([^"]*)"/, 'style="$1;visibility:hidden!important"')
        : open.replace(/>$/, ' style="visibility:hidden!important">');
      // Inline-styled thin coloured divs
      result = result.replace(
        /(<div\s+style="[^"]*background-color\s*:[^"]*)("\s*>\s*<\/div>)/gi,
        (match, stylePart, closeQuote) => /(height|width)\s*:\s*[1-8]px/.test(stylePart)
          ? stylePart + ';visibility:hidden!important' + closeQuote : match
      );
      // Class-based thin coloured divs — any class whose CSS rule is a line, not only title-line/divider
      const lineClasses = new Set(['title-line', 'divider']);
      const css = (result.match(/<style[^>]*>[\s\S]*?<\/style>/gi) || []).join('');
      for (const m of css.matchAll(/\.([\w-]+)\s*\{([^}]*)\}/g)) {
        if (/background(-color)?\s*:/.test(m[2]) && /(^|;)\s*(height|width)\s*:\s*[1-8]px/.test(m[2])) lineClasses.add(m[1]);
      }
      result = result.replace(/(<div\b[^>]*\bclass="([^"]*)"[^>]*>)\s*<\/div>/gi, (match, open: string, cls: string) => {
        if (!cls.split(/\s+/).some(c => lineClasses.has(c))) return match;
        if (/visibility\s*:\s*hidden/.test(open)) return match;
        return `${hideTag(open)}</div>`;
      });
    }

    for (const el of elements) {
      const pos = el.position;

      // Handle IMAGE elements — inject user-added images (skip template variable images like {{logo_url}})
      if (el.type === CanvasElementType.IMAGE) {
        const imgEl = el as ImageCanvasElement;
        if (!imgEl.src || imgEl.src.startsWith('{{')) continue;
        if (el.visible === false) continue;
        const wrapStyle = [
          `position:absolute`,
          `left:${Math.round(pos.x)}px`,
          `top:${Math.round(pos.y)}px`,
          `width:${Math.round(pos.width)}px`,
          `height:${Math.round(pos.height)}px`,
          `overflow:hidden`,
          `z-index:${pos.zIndex || 1}`,
          // keep the opacity set on canvas (e.g. a 15% watermark)
          ...(el.opacity !== undefined && el.opacity < 1 ? [`opacity:${el.opacity}`] : [])
        ].join(';');
        newElementHtmlParts.push(
          `<div data-canvas-injected style="${wrapStyle}"><img src="${imgEl.src}" alt="${imgEl.alt || ''}" style="width:100%;height:100%;object-fit:${imgEl.fit || 'contain'}" /></div>`
        );
        continue;
      }

      // Handle SHAPE elements (rectangle, circle, etc.)
      if (el.type === CanvasElementType.SHAPE) {
        const shapeEl = el as ShapeCanvasElement;
        const borderRadius = shapeEl.shapeType === 'circle' || shapeEl.shapeType === 'ellipse'
          ? '50%' : `${shapeEl.border?.radius || 0}px`;
        const borderCss = shapeEl.border
          ? `;border:${shapeEl.border.width}px ${shapeEl.border.style} ${shapeEl.border.color}` : '';
        const shapeStyle = [
          `position:absolute`,
          `left:${Math.round(pos.x)}px`,
          `top:${Math.round(pos.y)}px`,
          `width:${Math.round(pos.width)}px`,
          `height:${Math.round(pos.height)}px`,
          `background:${shapeEl.fill}`,
          `border-radius:${borderRadius}`,
          `z-index:${pos.zIndex || 1}`
        ].join(';') + borderCss;
        newElementHtmlParts.push(`<div data-canvas-injected style="${shapeStyle}"></div>`);
        continue;
      }

      // Handle LINE elements — always inject from canvas state and hide originals.
      // Previous approach (only hide+inject changed lines) used a GLOBAL hide regex that
      // hid ALL original decorative lines when any single line changed, but only injected
      // a replacement for the changed one — causing unchanged lines to disappear.
      // Fix: always inject ALL lines from canvas and hide ALL originals once.
      if (el.type === CanvasElementType.LINE) {
        const lineEl = el as LineCanvasElement;

        // Originals already hidden once above (linesManaged); always inject the line from canvas state
        if (el.visible === false) continue;
        const dashStyle = lineEl.strokeStyle === 'dashed' ? 'dashed'
          : lineEl.strokeStyle === 'dotted' ? 'dotted' : 'solid';
        const isVertical = pos.height > pos.width;
        const rotationCss = pos.rotation ? `;transform:rotate(${pos.rotation}deg)` : '';
        const lineStyle = isVertical ? [
          `position:absolute`,
          `left:${Math.round(pos.x)}px`,
          `top:${Math.round(pos.y)}px`,
          `width:0`,
          `height:${Math.round(pos.height)}px`,
          `border-left:${lineEl.strokeWidth}px ${dashStyle} ${lineEl.strokeColor}`,
          `z-index:${pos.zIndex || 1}`
        ].join(';') : [
          `position:absolute`,
          `left:${Math.round(pos.x)}px`,
          `top:${Math.round(pos.y)}px`,
          `width:${Math.round(pos.width)}px`,
          `height:0`,
          `border-top:${lineEl.strokeWidth}px ${dashStyle} ${lineEl.strokeColor}`,
          `z-index:${pos.zIndex || 1}`
        ].join(';');
        newElementHtmlParts.push(`<div data-canvas-injected style="${lineStyle}${rotationCss}"></div>`);
        continue;
      }

      // QR code / signature added from Quick Add (were dropped on save before). One wrapper div,
      // no nested divs, so the next save's data-canvas-injected strip removes it cleanly.
      if (el.type === CanvasElementType.QR_CODE || el.type === CanvasElementType.SIGNATURE) {
        if (el.visible === false) continue;
        const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const boxStyle = [
          `position:absolute`, `left:${Math.round(pos.x)}px`, `top:${Math.round(pos.y)}px`,
          `width:${Math.round(pos.width)}px`, `height:${Math.round(pos.height)}px`,
          `display:flex`, `flex-direction:column`, `align-items:center`, `justify-content:center`,
          `z-index:${pos.zIndex || 1}`,
          ...(el.opacity !== undefined && el.opacity < 1 ? [`opacity:${el.opacity}`] : [])
        ].join(';');
        if (el.type === CanvasElementType.QR_CODE) {
          // The backend renders the verification QR as {{qr_code_data_url}}
          const size = Math.round(Math.min((el as QRCodeCanvasElement).size || pos.width, pos.width, pos.height));
          newElementHtmlParts.push(`<div data-canvas-injected style="${boxStyle}"><img src="{{qr_code_data_url}}" alt="QR Code" style="width:${size}px;height:${size}px" /></div>`);
        } else {
          const sig = el as SignatureCanvasElement;
          const label = sig.showLabel && sig.label
            ? `<span style="display:block;margin-top:4px;padding-top:4px;border-top:1px solid #9CA3AF;font-family:Arial,sans-serif;font-size:11px;color:#6B7280;text-align:center;white-space:nowrap">${esc(sig.label)}</span>`
            : '';
          const mark = sig.signatureType === 'image' && sig.src
            ? `<img src="${esc(sig.src)}" alt="Signature" style="max-width:100%;max-height:70%;object-fit:contain" />`
            : `<span style="display:block;text-align:center;white-space:nowrap;font-family:${(sig.style?.fontFamily || 'Georgia, serif').replace(/"/g, "'")};font-size:${sig.style?.fontSize || 24}px;color:${sig.style?.color || '#111827'}">${esc(sig.text || '')}</span>`;
          newElementHtmlParts.push(`<div data-canvas-injected style="${boxStyle}">${mark}${label}</div>`);
        }
        continue;
      }

      if (el.type !== CanvasElementType.TEXT && el.type !== CanvasElementType.VARIABLE) continue;

      const key = el.type === CanvasElementType.TEXT
        ? (el as TextCanvasElement).content
        : (el as VariableCanvasElement).placeholder || `{{${(el as VariableCanvasElement).variableKey}}}`;

      // First element with this key that exists in initialElementStyles is the original — skip it,
      // BUT only if the key actually appears in the stripped HTML. Elements that only existed as
      // data-canvas-injected divs will have been stripped by stripCanvasComments and MUST be re-injected.
      // Also force-inject elements whose position changed — the original is hidden (visibility:hidden)
      // and needs a correctly-positioned replacement at body level.
      if (initialElementStyles[key] && !claimedKeys.has(key) && !posChangedKeys.has(key)) {
        const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (new RegExp(escapedKey).test(result)) {
          claimedKeys.add(key);
          continue;
        }
      }

      const style = (el as TextCanvasElement | VariableCanvasElement).style;
      const content = el.type === CanvasElementType.TEXT
        ? (el as TextCanvasElement).content
        : `{{${(el as VariableCanvasElement).variableKey}}}`;

      // Resolve backgroundColor, borderRadius, padding — from canvas state OR CSS class fallback
      let bgColor = style.backgroundColor || '';
      // 0 set in Corner Radius means square: don't fall back to the CSS class's rounding
      let bdRadius = style.borderRadius != null ? `${style.borderRadius}px` : '';
      let pad = style.padding ? `${style.padding}px` : '';

      // For old CANVAS_LAYOUT saves missing these properties, extract from original CSS
      if (!bgColor || !bdRadius) {
        const escaped2 = content.replace(/[{}.*+?^$|[\]\\()]/g, '\\$&');
        const classMatch = result.match(new RegExp(`class="([^"]*)"[^>]*>\\s*${escaped2}\\s*</`, 'i'));
        if (classMatch) {
          const cls = classMatch[1];
          if (!bgColor) {
            const bgRule = result.match(new RegExp(`\\.${cls}\\s*\\{[^}]*background\\s*:\\s*([^;}"]+)`, 'i'));
            if (bgRule) bgColor = bgRule[1].trim();
          }
          if (!bdRadius) {
            const brRule = result.match(new RegExp(`\\.${cls}\\s*\\{[^}]*border-radius\\s*:\\s*([^;}"]+)`, 'i'));
            if (brRule) bdRadius = brRule[1].trim();
          }
          if (!pad) {
            const padRule = result.match(new RegExp(`\\.${cls}\\s*\\{[^}]*padding\\s*:\\s*([^;}"]+)`, 'i'));
            if (padRule) pad = padRule[1].trim();
          }
        }
      }

      const styleParts = [
        `position:absolute`,
        `left:${Math.round(pos.x)}px`,
        `top:${Math.round(pos.y)}px`,
        `width:${Math.round(pos.width)}px`,
        `height:${Math.round(pos.height)}px`,
        `font-family:${(style.fontFamily || '').replace(/"/g, "'")}`,
        `font-size:${style.fontSize}px`,
        `font-weight:${style.fontWeight}`,
        `font-style:${style.fontStyle}`,
        `text-decoration:${style.textDecoration}`,
        `text-align:${style.textAlign}`,
        `color:${style.color}`,
        `letter-spacing:${style.letterSpacing}px`,
        `line-height:${style.lineHeight}`,
        `display:flex`,
        `align-items:center`,
        `justify-content:center`,
        `z-index:${pos.zIndex || 1}`
      ];
      // Static text: match the canvas (one line, same alignment) so a long title in a
      // narrow box doesn't wrap into several lines in the PDF. Variables keep wrapping
      // because their real value (e.g. certificate number) is longer than the placeholder.
      if (el.type === CanvasElementType.TEXT) {
        const justify = style.textAlign === 'right' ? 'flex-end' : style.textAlign === 'left' ? 'flex-start' : 'center';
        styleParts[styleParts.indexOf('justify-content:center')] = `justify-content:${justify}`;
        styleParts.push('white-space:nowrap');
      }
      if (el.opacity !== undefined && el.opacity < 1) styleParts.push(`opacity:${el.opacity}`);
      if (bgColor) styleParts.push(`background-color:${bgColor}`);
      if (bdRadius) styleParts.push(`border-radius:${bdRadius}`);
      if (pad) styleParts.push(`padding:${pad}`);
      const inlineStyle = styleParts.join(';');

      // Logo URL variables → output as plain text so backend wraps in <img> with proper styling.
      // Use minimum 150x50 container so the logo isn't tiny.
      if (el.type === CanvasElementType.VARIABLE && (el as VariableCanvasElement).variableKey.endsWith('_logo_url')) {
        const logoW = Math.max(pos.width, 150);
        const logoH = Math.max(pos.height, 50);
        const wrapStyle = `position:absolute;left:${Math.round(pos.x)}px;top:${Math.round(pos.y)}px;width:${Math.round(logoW)}px;height:${Math.round(logoH)}px;z-index:${pos.zIndex || 1}`;
        newElementHtmlParts.push(`<div data-canvas-injected style="${wrapStyle}">${content}</div>`);
      } else {
        // Typed text is plain text: "R&D <Team>" must not turn into HTML ({{variables}} are unaffected)
        const html = el.type === CanvasElementType.TEXT
          ? content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          : content;
        newElementHtmlParts.push(`<div data-canvas-injected style="${inlineStyle}">${html}</div>`);
      }
    }

    // Hide original <img src="{{var}}"> tags (logos, QR code) by src attribute — the
    // text-content regex can't match them because the variable sits in src, not between tags.
    const hideVariableImage = (key: string) => {
      const varMatch = key.match(/^\{\{(\w+)\}\}$/);
      if (!varMatch) return;
      const varName = varMatch[1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result.replace(
        new RegExp(`(<img\\b[^>]*?src=["']\\{\\{${varName}\\}\\}["'][^>]*?)(/?>)`, 'gi'),
        (_m, before, close) => {
          if (/style\s*=\s*"/.test(before)) {
            return before.replace(/style\s*=\s*"([^"]*)"/, 'style="$1;visibility:hidden!important"') + close;
          }
          return before + ' style="visibility:hidden!important"' + close;
        }
      );
    };
    // Moved on canvas → original hidden, the re-injected copy is the only one left.
    // Was limited to *_logo_url, so a moved QR code printed twice.
    for (const key of posChangedKeys) hideVariableImage(key);

    // Hide orphaned original elements — elements from the initial parse that the user
    // deleted or renamed in the canvas. Uses per-element text matching to hide individual
    // elements without affecting the rest of the template.
    const canvasKeys = new Set<string>();
    for (const el of elements) {
      if (el.type === CanvasElementType.TEXT) {
        canvasKeys.add((el as TextCanvasElement).content);
      } else if (el.type === CanvasElementType.VARIABLE) {
        const varEl = el as VariableCanvasElement;
        canvasKeys.add(varEl.placeholder || `{{${varEl.variableKey}}}`);
      }
    }
    for (const origKey of Object.keys(initialElementStyles)) {
      if (canvasKeys.has(origKey)) continue; // still in canvas — leave it
      if (posChangedKeys.has(origKey)) continue; // already hidden by position-change logic
      hideVariableImage(origKey); // deleted QR / logo variable → hide its <img src="{{var}}">
      const escaped = origKey.replace(/[{}.*+?^$|[\]\\()]/g, '\\$&');
      const pattern = new RegExp(`(<[a-z][a-z0-9]*\\b[^>]*?)(>\\s*${escaped}\\s*</)`, 'gi');
      // visibility:hidden keeps its space, so the elements below don't slide up
      result = result.replace(pattern, (_, openTag: string, rest: string) => {
        const existingStyle = openTag.match(/style="([^"]*)"/);
        if (existingStyle) {
          return `${openTag.replace(/style="[^"]*"/, `style="${existingStyle[1]};visibility:hidden !important"`)}${rest}`;
        }
        return `${openTag} style="visibility:hidden !important"${rest}`;
      });
    }

    if (newElementHtmlParts.length > 0) {
      const newHtml = newElementHtmlParts.join('\n');
      // Insert inside .certificate (NOT .content) — canvas coordinates are relative to
      // .certificate, so absolute positioning must reference that container.
      // .content has padding that would offset the positions incorrectly.
      if (result.includes('class="certificate"')) {
        result = result.replace(/(class="certificate"[^>]*>)/, `$1\n${newHtml}`);
      } else {
        result = result.replace('</body>', `${newHtml}\n</body>`);
      }
    }

    // Only modify borders if user actually changed border properties via Properties Panel.
    // This preserves original template borders EXACTLY — prevents color/style drift from
    // parser rounding or computed style differences across ALL certificate templates.
    const currentBorder = layoutData.canvas.decorativeBorder;
    const bordersChanged = JSON.stringify(currentBorder || {}) !== initialBorderJson;

    if (bordersChanged && currentBorder) {
      // Strip inline-styled decorative border divs to prevent duplication.
      // Only done when we're about to inject class-based replacements.
      result = result.replace(
        /<div\s+style="[^"]*?position\s*:\s*absolute[^"]*?\bborder[^"]*?">\s*<\/div>/gi,
        ''
      );

      // Apply decorative border changes — replace entire CSS rules for reliability
      const htmlHasOuterBorder = /class=["'][^"']*?\bouter-border\b/.test(result);
      const htmlHasInnerBorder = /class=["'][^"']*?\binner-border\b/.test(result);
      const htmlHasCorners = /class=["'][^"']*?\bcorner\b/.test(result);

      const replaceOrInjectCss = (selectorClass: string, newRule: string) => {
        const regex = new RegExp(`\\.${selectorClass}\\s*\\{[^}]*\\}`, 'g');
        const originalResult = result;
        result = result.replace(regex, newRule);

        if (result === originalResult) {
          if (result.includes('</style>')) {
            result = result.replace('</style>', newRule + '\n</style>');
          } else if (result.includes('</head>')) {
            result = result.replace('</head>', `\n<style>\n${newRule}\n</style>\n</head>`);
          } else {
            result += `\n<style>\n${newRule}\n</style>`;
          }
        }
      };

      if (currentBorder.outer) {
        const o = currentBorder.outer;
        const css = o.enabled
          ? `.outer-border{position:absolute;top:${o.inset.top}px;left:${o.inset.left}px;right:${o.inset.right}px;bottom:${o.inset.bottom}px;border:${o.width}px ${o.style} ${o.color};pointer-events:none;}`
          : `.outer-border{display:none;}`;
        replaceOrInjectCss('outer-border', css);
      }
      if (currentBorder.inner) {
        const n = currentBorder.inner;
        const css = n.enabled
          ? `.inner-border{position:absolute;top:${n.inset.top}px;left:${n.inset.left}px;right:${n.inset.right}px;bottom:${n.inset.bottom}px;border:${n.width}px ${n.style} ${n.color};pointer-events:none;}`
          : `.inner-border{display:none;}`;
        replaceOrInjectCss('inner-border', css);
      }
      if (currentBorder.corners) {
        const c = currentBorder.corners;
        if (!c.enabled) {
          replaceOrInjectCss('corner', `.corner{display:none;}`);
        } else {
          replaceOrInjectCss('corner', `.corner{position:absolute;width:${c.size}px;height:${c.size}px;border-color:${c.color};border-style:solid;pointer-events:none;}`);
          replaceOrInjectCss('corner-tl', `.corner-tl{top:${c.inset.top}px;left:${c.inset.left}px;border-width:${c.width}px 0 0 ${c.width}px;}`);
          replaceOrInjectCss('corner-tr', `.corner-tr{top:${c.inset.top}px;right:${c.inset.right}px;border-width:${c.width}px ${c.width}px 0 0;}`);
          replaceOrInjectCss('corner-bl', `.corner-bl{bottom:${c.inset.bottom}px;left:${c.inset.left}px;border-width:0 0 ${c.width}px ${c.width}px;}`);
          replaceOrInjectCss('corner-br', `.corner-br{bottom:${c.inset.bottom}px;right:${c.inset.right}px;border-width:0 ${c.width}px ${c.width}px 0;}`);
        }
      }

      // --- INJECT HTML DIVs ONLY when they don't exist ---
      let borderHtml = '';
      if (currentBorder.outer?.enabled && !htmlHasOuterBorder) {
        borderHtml += '<div class="outer-border"></div>\n';
      }
      if (currentBorder.inner?.enabled && !htmlHasInnerBorder) {
        borderHtml += '<div class="inner-border"></div>\n';
      }
      if (currentBorder.corners?.enabled && !htmlHasCorners) {
        borderHtml += '<div class="corner corner-tl"></div>\n<div class="corner corner-tr"></div>\n<div class="corner corner-bl"></div>\n<div class="corner corner-br"></div>\n';
      }

      if (borderHtml) {
        if (result.includes('class="certificate"')) {
          result = result.replace(/(class="certificate"[^>]*>)/, `$1\n${borderHtml}`);
        } else {
          result = result.replace(/<body[^>]*>/, `$&\n${borderHtml}`);
        }
      }
    }
    // When borders are unchanged: original HTML borders stay EXACTLY as-is.
    // No stripping, no CSS injection, no color drift.

    // Apply canvas background if user changed it (solid, gradient, or image)
    const currentBg = layoutData.canvas.background;
    const bgChanged = JSON.stringify(currentBg || {}) !== initialBackgroundJson;
    if (bgChanged && currentBg) {
      let bgCss = '';
      if (currentBg.type === 'solid' && currentBg.color) {
        bgCss = `background:${currentBg.color} !important`;
      } else if (currentBg.type === 'gradient' && currentBg.gradient) {
        const stops = currentBg.gradient.colors.map(c => `${c.color} ${c.position}%`).join(',');
        bgCss = currentBg.gradient.type === 'linear'
          ? `background:linear-gradient(${currentBg.gradient.angle || 0}deg,${stops}) !important`
          : `background:radial-gradient(circle,${stops}) !important`;
      } else if (currentBg.type === 'image' && currentBg.image?.src) {
        bgCss = `background-image:url(${currentBg.image.src}) !important;background-size:${currentBg.image.fit === 'tile' ? 'auto' : currentBg.image.fit} !important;background-repeat:${currentBg.image.fit === 'tile' ? 'repeat' : 'no-repeat'} !important;background-position:center !important`;
      }
      if (bgCss) {
        // Apply to body and .certificate (whichever the template uses as container)
        let bgRule = `body{${bgCss}}\n.certificate{${bgCss}}`;
        // Full-page boxes like ".cert": a solid colour sets only the fill, so watermark images stay
        const pageBoxes = findPageBoxSelectors(result);
        if (pageBoxes.length) {
          const boxCss = currentBg.type === 'solid' ? `background-color:${currentBg.color} !important` : bgCss;
          bgRule += `\n${pageBoxes.join(',')}{${boxCss}}`;
        }
        if (result.includes('</style>')) {
          result = result.replace('</style>', bgRule + '\n</style>');
        } else if (result.includes('</head>')) {
          result = result.replace('</head>', `\n<style>\n${bgRule}\n</style>\n</head>`);
        } else {
          result += `\n<style>\n${bgRule}\n</style>`;
        }
      }
    }

    // Apply simple canvas border (width, style, color, radius) if set by user.
    // Only when changed: the parser fills it in on first open, and writing it unasked added an edge
    // and shifted the whole design 2px. Templates saved before this (no initial value) keep the old rule.
    const simpleBorder = layoutData.canvas.border;
    const simpleBorderChanged = initialSimpleBorderJson === undefined || JSON.stringify(simpleBorder || {}) !== initialSimpleBorderJson;
    if (simpleBorder && simpleBorder.width > 0 && simpleBorderChanged) {
      const borderCss = `border:${simpleBorder.width}px ${simpleBorder.style} ${simpleBorder.color} !important${simpleBorder.radius ? `;border-radius:${simpleBorder.radius}px !important` : ''}`;
      const borderRule = `body{${borderCss}}\n.certificate{${borderCss}}`;
      if (result.includes('</style>')) {
        result = result.replace('</style>', borderRule + '\n</style>');
      } else if (result.includes('</head>')) {
        result = result.replace('</head>', `\n<style>\n${borderRule}\n</style>\n</head>`);
      } else {
        result += `\n<style>\n${borderRule}\n</style>`;
      }
    }

    // Ensure no scrollbars in preview/final HTML + position:relative on container
    // for absolute-positioned elements to reference correctly
    const noScrollCss = `body { margin: 0 !important; padding: 0 !important; overflow: hidden !important; }
.certificate { position: relative !important; }
body { position: relative !important; }`;
    if (!result.includes('overflow: hidden !important')) {
      if (result.includes('</style>')) {
        result = result.replace('</style>', noScrollCss + '\n</style>');
      } else if (result.includes('</head>')) {
        result = result.replace('</head>', `\n<style>\n${noScrollCss}\n</style>\n</head>`);
      } else {
        result += `\n<style>\n${noScrollCss}\n</style>`;
      }
    }


    return result;
  }, [initialElementStyles, layoutData.canvas.decorativeBorder, initialBorderJson, layoutData.canvas.background, initialBackgroundJson, layoutData.canvas.border, initialSimpleBorderJson]);

  // Save handler — apply style changes to original HTML + append CANVAS_LAYOUT
  const handleSave = useCallback(() => {
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    // New template: never save an empty page
    if (isCreateMode && !layoutData.elements.some(el => el.visible !== false)) {
      toast.error('Add at least one element before saving.');
      return;
    }

    let htmlTemplate: string;

    // Use pristine HTML (clean original, before any canvas modifications) as the base.
    // This prevents cumulative style drift — each save applies changes fresh to the clean original.
    const baseHtml = pristineHtmlTemplate || originalHtmlTemplate;
    if (baseHtml) {
      // EDIT MODE: Start with CLEAN original HTML, apply user's style changes, append CANVAS_LAYOUT
      // Use stripCanvasComments (NOT stripEditorArtifacts) to preserve {{#if}}/{{else}} blocks
      let modifiedHtml = stripCanvasComments(baseHtml);

      // Apply canvas element style changes (color, font, size, etc.) to matching HTML elements
      modifiedHtml = applyCanvasStylesToHtml(modifiedHtml, layoutData.elements);

      // Embed pristine original HTML (base64) so future loads start from a clean base.
      // This prevents cumulative degradation across save→reload→save cycles.
      // Use encodeURIComponent + unescape to safely handle Unicode characters in btoa
      const pristineComment = `\n<!--ORIGINAL_TEMPLATE:${btoa(unescape(encodeURIComponent(baseHtml)))}:END_ORIGINAL_TEMPLATE-->`;

      // Append CANVAS_LAYOUT (with initial state for correct change detection on reload)
      const layoutWithInitialStyles = {
        ...layoutData,
        _initialElementStyles: initialElementStyles,
        _initialBorderJson: initialBorderJson,
        _initialBackgroundJson: initialBackgroundJson,
        _initialSimpleBorderJson: initialSimpleBorderJson
      };
      const layoutComment = `\n<!--CANVAS_LAYOUT:${JSON.stringify(layoutWithInitialStyles)}:END_CANVAS_LAYOUT-->`;
      if (modifiedHtml.includes('</body>')) {
        htmlTemplate = modifiedHtml.replace('</body>', pristineComment + layoutComment + '\n</body>');
      } else {
        htmlTemplate = modifiedHtml + pristineComment + layoutComment;
      }
    } else {
      // NEW TEMPLATE or no original: generate from canvas elements
      htmlTemplate = buildCanvasHtml(layoutData);
    }

    // Preserve original allowed_variables + any new variables from canvas
    const canvasVars = extractVariablesFromLayout(layoutData);
    const originalVars = existingTemplate?.allowed_variables || [];
    const allowedVariables = [...new Set([...originalVars, ...canvasVars])];

    // is_active / is_default are not sent: the editor has no control for them, and re-sending
    // the values loaded at open time would undo a switch made from the list meanwhile.
    const payload = {
      template_name: templateName,
      template_type: templateType,
      html_template: htmlTemplate,
      allowed_variables: allowedVariables
    };
    if (isCreateMode) {
      if (!createMutation.isPending) createMutation.mutate({ ...payload, template_type: templateType! });
      return;
    }
    updateMutation.mutate(payload);
  }, [templateName, templateType, isCreateMode, existingTemplate, layoutData, originalHtmlTemplate, pristineHtmlTemplate, initialElementStyles, initialBorderJson, initialBackgroundJson, initialSimpleBorderJson, applyCanvasStylesToHtml, updateMutation, createMutation]);

  const goToList = useCallback(() => {
    navigate('/admin/enterprise/certificate-templates');
  }, [navigate]);

  // A new template that was changed but never saved → confirm before throwing it away
  const handleBack = useCallback(() => {
    if (isCreateMode && history.past.length > 0) {
      setShowDiscardConfirm(true);
      return;
    }
    goToList();
  }, [isCreateMode, history.past.length, goToList]);

  // Same protection for refresh / closing the tab (the browser shows its own "Leave site?" prompt)
  const hasUnsavedNewTemplate = isCreateMode && history.past.length > 0 && !createMutation.isPending;
  useEffect(() => {
    if (!hasUnsavedNewTemplate) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedNewTemplate]);

  if (isEditMode ? savedTemplateFailed : sourceTemplateFailed) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <p className="text-gray-700 mb-4">Could not load the template.</p>
          <button onClick={goToList} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">
            Back to templates
          </button>
        </div>
      </div>
    );
  }

  if ((isEditMode || isCreateMode) && (!existingTemplate || !templateReady)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading template...</p>
        </div>
      </div>
    );
  }

  // Create route without "Create Template" state — the effect above redirects
  if (!isEditMode && !isCreateMode) return null;

  const isSaving = updateMutation.isPending || createMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-100">
      {/* Top Toolbar */}
      <EditorToolbar
        templateName={templateName}
        templateType={templateType!}
        isEditMode={isEditMode}
        isSaving={isSaving}
        canUndo={history.past.length > 0}
        canRedo={history.future.length > 0}
        hasSelectedElement={!!selectedElementId}
        onBack={handleBack}
        onSave={() => handleSave()}
        onPreview={() => setShowPreview(true)}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onDuplicate={() => selectedElementId && handleDuplicateElement(selectedElementId)}
        onDelete={() => selectedElementId && handleDeleteElement(selectedElementId)}
        onClearContent={handleClearContent}
        onRestoreContent={isCleared && originalHtmlTemplate ? handleRestoreContent : undefined}
        onRename={templateReady ? handleRename : undefined}
      />

      {/* Main Content - Panel Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Elements */}
        {templateType && (
          <ElementsPanel
            templateType={templateType}
            onAddElement={handleAddElement}
          />
        )}

        {/* Center - Canvas Area */}
        <div className={`flex-1 flex flex-col overflow-hidden${isBlankStart ? ' relative' : ''}`} id="certificate-canvas-export">
          <CertificateCanvas
            layoutData={layoutData}
            selectedElementId={selectedElementId}
            sampleData={sampleData}
            backgroundHtml={backgroundHtml}
            onSelectElement={setSelectedElementId}
            onUpdateElement={handleUpdateElement}
            onDeleteElement={handleDeleteElement}
            onDuplicateElement={handleDuplicateElement}
            onUpdateCanvas={handleUpdateCanvas}
            onDropElement={handleDropElement}
          />
          {/* Blank canvas hint — clicks pass through so drag & drop still works */}
          {isBlankStart && layoutData.elements.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="blank-canvas-hint">
              <div className="bg-white/95 rounded-xl shadow-lg border border-gray-200 px-6 py-5 text-center max-w-sm">
                <p className="text-sm font-semibold text-gray-900">Your certificate is blank</p>
                <p className="text-xs text-gray-500 mt-1">
                  Start with <span className="font-medium text-gray-700">Certificate Title</span> and <span className="font-medium text-gray-700">Recipient Name</span> from Quick Add on the left, then add dates, a QR code or a signature.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Properties */}
        <PropertiesPanel
          selectedElement={selectedElement}
          canvasConfig={layoutData.canvas}
          onUpdateElement={handleUpdateElement}
          onUpdateCanvas={handleUpdateCanvas}
        />
      </div>

      {/* Preview Modal — shows actual template with user's style changes applied */}
      {showPreview && (() => {
     let previewHtml: string;

     const previewBase = pristineHtmlTemplate || originalHtmlTemplate;
     if (previewBase) {
       previewHtml = stripEditorArtifacts(previewBase);
       previewHtml = applyCanvasStylesToHtml(previewHtml, layoutData.elements);
     } else {
       previewHtml = buildCanvasHtml(layoutData);
     }
        return (
          <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-8">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-green-500" />
                  <h3 className="text-lg font-semibold text-gray-900">Certificate Preview</h3>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-8 bg-gray-100 flex items-center justify-center">
                <div
                  className="shadow-2xl rounded-lg overflow-hidden"
                  style={{ width: layoutData.canvas.width * 0.85, height: layoutData.canvas.height * 0.85 }}
                >
                  <iframe
                    srcDoc={previewHtml}
                    title="Certificate Preview"
                    className="border-0 bg-white"
                    sandbox="allow-same-origin"
                    style={{
                      width: layoutData.canvas.width,
                      height: layoutData.canvas.height,
                      transform: 'scale(0.85)',
                      transformOrigin: 'top left',
                      display: 'block'
                    }}
                  />
                </div>
              </div>
              <div className="p-4 border-t bg-gray-50 text-center text-sm text-gray-500">
                Showing actual template. Variables like &#123;&#123;holder_name&#125;&#125; will be replaced with real values.
              </div>
            </div>
          </div>
        );
      })()}

      <UseTemplateConfirmModal
        isOpen={showUseConfirm}
        template={id && templateType ? { _id: id, template_name: templateName, template_type: templateType } : null}
        onClose={() => setShowUseConfirm(false)}
      />

      <ConfirmationModal
        isOpen={showDiscardConfirm}
        title="Leave without saving?"
        message="This new template has not been saved. If you leave now, it will not be created."
        type="warning"
        confirmText="Leave without saving"
        cancelText="Keep editing"
        onConfirm={goToList}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </div>
  );
}
